import React, { useState, useRef } from 'react';
import { Input } from './ui/input';
import { AlertTriangle, Copy, Plus, Info } from 'lucide-react';
import TimeField from './TimeField';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// day | AM group | break gap | PM group | hours | actions — a slimmer table (Rob, 03/11/2026:
// "a bit chunky"): one line between days rather than a card each, and room to fit narrower boxes.
const GRID = { display: 'grid', gridTemplateColumns: '52px minmax(0,1fr) 70px minmax(0,1fr) 64px 118px', alignItems: 'center', columnGap: '10px', minWidth: '760px' };

const safeParse = (s) => { try { return JSON.parse(s); } catch (e) { return null; } };

const toMins = (t) => { const [h, m] = (t || '0:0').split(':').map(Number); return (h || 0) * 60 + (m || 0); };
const toHHMM = (mins) => `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;

const normDay = (d, weekday) => {
  if (!d) {
    // Default for a BRAND-NEW pattern: a 7-hour day with an hour for lunch.
    // This is a deliberate default for data that doesn't exist yet, not an
    // assumption imposed on data that does (see the legacy location below).
    return weekday
      ? { am: { enabled: true, start: '09:00', end: '12:00' }, pm: { enabled: true, start: '13:00', end: '17:00' } }
      : { am: { enabled: false, start: '09:00', end: '12:00' }, pm: { enabled: false, start: '13:00', end: '17:00' } };
  }
  if (d.am || d.pm) {
    const out = {
      am: { enabled: false, start: '09:00', end: '12:00', ...(d.am || {}) },
      pm: { enabled: false, start: '13:00', end: '17:00', ...(d.pm || {}) },
    };
    // An explicitly PAID break (working time, no gap). MUST be carried through
    // normalisation or every edit would silently strip it from the pattern —
    // the same class of data-destroying bug the save endpoint had with am/pm.
    const paid = Math.max(0, parseInt(d.paid_break_minutes, 10) || 0);
    if (paid > 0) out.paid_break_minutes = paid;
    return out;
  }
  // LEGACY FLAT SHAPE -> AM/PM. Split the stored span at its MIDPOINT.
  //
  // This previously hard-coded am = start..12:00 and pm = 13:00..end, which
  // INVENTED a 12:00-13:00 lunch break that was never in the data: a stored
  // 09:00-17:00 (8h) silently became 3h + 4h = 7h the moment anyone opened this
  // editor. Contracted hours must survive a representation change untouched, so
  // we split at the midpoint instead and preserve the span exactly.
  //   09:00-17:00 (8h)  ->  am 09:00-13:00 (4h) + pm 13:00-17:00 (4h)  = 8h
  const startStr = d.start || '09:00';
  const endStr = d.end || '17:00';
  const start = toMins(startStr);
  const end = toMins(endStr);
  // Degenerate span (end <= start): collapse the midpoint onto the START so the day is
  // worth zero. Falling back to a literal '13:00' would manufacture a bogus 09:00-13:00
  // morning out of a zero-length day. Must stay identical to services/schedule.py's
  // _norm_day — if the two disagree, an employee's contracted hours would depend on
  // which side of the wire you ask.
  const mid = end > start ? toHHMM(start + Math.round((end - start) / 2)) : startStr;
  return {
    am: { enabled: !!d.enabled, start: startStr, end: mid },
    pm: { enabled: !!d.enabled, start: mid, end: endStr },
  };
};

// Weekly hours implied by a pattern (used to auto-fill the "Hours per week" field).
export const computeWeeklyHours = (hours, companyHours) => {
  const source = (typeof hours === 'string' ? safeParse(hours) : hours) || companyHours || null;
  const toMins = (t) => { const [h, m] = (t || '0:0').split(':').map(Number); return (h || 0) * 60 + (m || 0); };
  let total = 0;
  DAYS.forEach((day, i) => {
    const d = normDay(source ? source[day] : null, i < 5);
    total += ((d.am.enabled ? Math.max(0, toMins(d.am.end) - toMins(d.am.start)) : 0)
            + (d.pm.enabled ? Math.max(0, toMins(d.pm.end) - toMins(d.pm.start)) : 0)) / 60;
  });
  return total;
};

// Human-friendly duration: 60 -> "1h", 45 -> "45m", 90 -> "1h 30m".
const fmtMins = (m) => {
  const h = Math.floor(m / 60), r = m % 60;
  return h > 0 ? (r > 0 ? `${h}h ${r}m` : `${h}h`) : `${r}m`;
};

// Place a break of `len` minutes ('unpaid' | 'paid') into one dual-session day,
// PRESERVING POSITION: the day's start and end NEVER move, and the break stays
// centred where the existing break is (or at the AM/PM boundary if there's no gap).
//
// The previous version re-split the whole span around its midpoint, which shuffled
// the session times every time the break changed — 09:00-12:00/13:00-17:00 with the
// break set to 80 became 09:00-12:20/13:40-17:00. Correct totals, baffling times.
// Centre-preserving placement keeps lunch where lunch actually is, and makes
// paid <-> unpaid an EXACT round trip:
//   09:00-12:00 / 13:00-17:00 (60 unpaid)
//     -> paid:   09:00-12:30 / 12:30-17:00 + 60 paid   (gap closes at its centre)
//     -> unpaid: 09:00-12:00 / 13:00-17:00             (reopens in the same place)
// A paid break is working time, so the sessions become contiguous and the minutes
// are recorded on the day (`paid_break_minutes`) instead. Changing the LENGTH of a
// paid break therefore changes no times at all. Returns {am, pm[, paid_break_minutes]},
// so any previous paid key is cleared unless re-set.
const placeBreak = (d, len, kind) => {
  const s = toMins(d.am.start);
  const e = toMins(d.pm.end);
  const gap = toMins(d.pm.start) - toMins(d.am.end);
  const centre = gap > 0
    ? Math.round((toMins(d.am.end) + toMins(d.pm.start)) / 2)
    : toMins(d.am.end);
  const n = Math.max(0, Math.min(Math.round(len || 0), Math.max(0, e - s)));
  let amEnd, pmStart;
  if (kind === 'paid' || n === 0) {
    const b = Math.min(Math.max(centre, s), e);   // contiguous sessions
    amEnd = b; pmStart = b;
  } else {
    amEnd = centre - Math.floor(n / 2);
    pmStart = amEnd + n;
    if (amEnd < s) { amEnd = s; pmStart = Math.min(e, s + n); }
    if (pmStart > e) { pmStart = e; amEnd = Math.max(s, e - n); }
  }
  const out = {
    am: { ...d.am, end: toHHMM(amEnd) },
    pm: { ...d.pm, start: toHHMM(pmStart) },
  };
  if (kind === 'paid' && n > 0) out.paid_break_minutes = n;
  return out;
};

// `warnings`: ADVISORY statutory rest-break warnings from the backend
// (POST /settings/working-hours/check). The employment law itself lives in
// services/jurisdictions.py — do NOT compute or hard-code it here (D9). An
// empty list may mean "compliant" OR "country not modelled"; both render as nothing.
const WorkingHoursEditor = ({ hours, onChange, companyHours, warnings = [], onBreakMinutesChange }) => {
  const source = (typeof hours === 'string' ? safeParse(hours) : hours) || companyHours || null;
  const schedule = {};
  DAYS.forEach((day, i) => { schedule[day] = normDay(source ? source[day] : null, i < 5); });

  // Digits mid-typing in the live break field (committed on idle / blur / Enter).
  const [pendingBreak, setPendingBreak] = useState(null);
  const breakTimer = useRef(null);

  const emit = (next) => onChange(JSON.stringify(next));
  const updBlock = (day, part, field, value) => emit({ ...schedule, [day]: { ...schedule[day], [part]: { ...schedule[day][part], [field]: value } } });
  const setFullDay = (day) => emit({ ...schedule, [day]: { am: { ...schedule[day].am, enabled: true }, pm: { ...schedule[day].pm, enabled: true } } });
  const setOff = (day) => emit({ ...schedule, [day]: { am: { ...schedule[day].am, enabled: false }, pm: { ...schedule[day].pm, enabled: false } } });
  // Copy this day's TIMES (and paid break) to the days below — but never its
  // enabled flags: "copy Monday down" must not silently put the weekend to work.
  const copyDown = (idx) => {
    const src = schedule[DAYS[idx]];
    const next = { ...schedule };
    for (let i = idx + 1; i < DAYS.length; i++) {
      const tgt = next[DAYS[i]];
      const copied = JSON.parse(JSON.stringify(src));
      copied.am.enabled = tgt.am.enabled;
      copied.pm.enabled = tgt.pm.enabled;
      next[DAYS[i]] = copied;
    }
    emit(next);
  };

  const mins = (t) => { const [h, m] = (t || '0:0').split(':').map(Number); return (h || 0) * 60 + (m || 0); };
  const dayHours = (d) => ((d.am.enabled ? Math.max(0, mins(d.am.end) - mins(d.am.start)) : 0) + (d.pm.enabled ? Math.max(0, mins(d.pm.end) - mins(d.pm.start)) : 0)) / 60;
  const totalHours = DAYS.reduce((sum, day) => sum + dayHours(schedule[day]), 0);
  const statusOf = (d) => (d.am.enabled && d.pm.enabled) ? 'Full day' : d.am.enabled ? 'Morning only' : d.pm.enabled ? 'Afternoon only' : 'Not working';

  // The break IS the gap between the AM and PM sessions (D10) — surface it rather
  // than imply it. Only exists when both sessions are worked; negative = overlap.
  const dayGap = (d) => (d.am.enabled && d.pm.enabled) ? mins(d.pm.start) - mins(d.am.end) : null;
  const dayPaid = (d) => Math.max(0, parseInt(d.paid_break_minutes, 10) || 0);
  const totalBreak = DAYS.reduce((sum, day) => { const g = dayGap(schedule[day]); return sum + (g && g > 0 ? g : 0); }, 0);
  const totalPaidBreak = DAYS.reduce((sum, day) => { const d = schedule[day]; return sum + ((d.am.enabled && d.pm.enabled) ? dayPaid(d) : 0); }, 0);

  // ---- The break, as a LIVE field --------------------------------------------
  // The field is DERIVED from the schedule (the break IS the schedule: gap = unpaid,
  // paid_break_minutes = paid) and edits apply straight back to every dual-session
  // day. There is deliberately no Apply button: a control that looks like a setting
  // must behave like one — the old pending-command version was mistaken for a field
  // twice in one day, both times by someone who built half the product.
  const dayBreakInfo = (d) => {
    if (!(d.am.enabled && d.pm.enabled)) return null;
    return {
      len: Math.max(0, dayGap(d)) + dayPaid(d),
      kind: dayPaid(d) > 0 ? 'paid' : 'unpaid',
    };
  };
  const breakInfos = DAYS.map((day) => dayBreakInfo(schedule[day])).filter(Boolean);
  const uniformLen = (breakInfos.length && breakInfos.every((i) => i.len === breakInfos[0].len)) ? breakInfos[0].len : null;
  const uniformKind = (breakInfos.length && breakInfos.every((i) => i.kind === breakInfos[0].kind)) ? breakInfos[0].kind : null;

  // Rewrite every dual-session day's break. len null = keep each day's own length;
  // kind null = keep each day's own kind (so a length edit on a mixed paid/unpaid
  // week doesn't flatten the kinds, and vice versa).
  const applyBreak = (len, kind) => {
    const next = {};
    DAYS.forEach((day) => {
      const d = schedule[day];
      const info = dayBreakInfo(d);
      if (!info) { next[day] = d; return; }
      const L = len === null ? info.len : len;
      const K = kind === null ? info.kind : kind;
      next[day] = placeBreak(d, L, K);
    });
    emit(next);
  };

  const commitBreakLen = (raw) => {
    if (breakTimer.current) { clearTimeout(breakTimer.current); breakTimer.current = null; }
    setPendingBreak(null);
    if (raw === '' || raw === null) return;   // nothing typed -> nothing to apply
    const n = Math.max(0, parseInt(raw, 10) || 0);
    applyBreak(n, uniformKind);               // preserves per-day kind when mixed
    onBreakMinutesChange?.(n);
  };
  const onBreakLenType = (v) => {
    setPendingBreak(v);
    if (breakTimer.current) clearTimeout(breakTimer.current);
    breakTimer.current = setTimeout(() => commitBreakLen(v), 700);
  };
  const onBreakKindChange = (k) => {
    if (k !== 'paid' && k !== 'unpaid') return;   // ignore the 'mixed' placeholder
    const len = pendingBreak !== null ? Math.max(0, parseInt(pendingBreak, 10) || 0) : null;
    if (breakTimer.current) { clearTimeout(breakTimer.current); breakTimer.current = null; }
    setPendingBreak(null);
    applyBreak(len, k);                       // len null = keep each day's length
  };

  // One session: its tick box and two time boxes. A FUNCTION, not a component defined in here —
  // a component made fresh every render would remount the time boxes and lose what is being typed.
  const renderBlock = (day, idx, part) => {
    const b = schedule[day][part];
    const name = `${LABELS[idx]} ${part === 'am' ? 'morning' : 'afternoon'}`;
    return (
      <div className="flex items-center justify-center gap-1.5">
        <input type="checkbox" checked={b.enabled} onChange={(e) => updBlock(day, part, 'enabled', e.target.checked)} className="w-4 h-4 rounded accent-primary flex-shrink-0" aria-label={`${name} worked`} />
        <TimeField value={b.start} disabled={!b.enabled} onChange={(v) => updBlock(day, part, 'start', v)} ariaLabel={`${name} starts`} />
        <span className="text-xs text-muted-foreground">&ndash;</span>
        <TimeField value={b.end} disabled={!b.enabled} onChange={(v) => updBlock(day, part, 'end', v)} ariaLabel={`${name} ends`} />
      </div>
    );
  };

  return (
    <div className="overflow-x-auto">
      <div style={GRID} className="px-1.5 pb-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
        <span>Day</span>
        <span className="text-center">Morning</span>
        <span className="text-center">Break</span>
        <span className="text-center">Afternoon</span>
        <span className="text-right">Hours</span>
        <span />
      </div>
      {DAYS.map((day, idx) => {
        const d = schedule[day];
        const st = statusOf(d);
        const working = d.am.enabled || d.pm.enabled;
        const gap = dayGap(d);
        const paid = (d.am.enabled && d.pm.enabled) ? dayPaid(d) : 0;
        const copy = idx < DAYS.length - 1 && (
          <button type="button" onClick={() => copyDown(idx)} title="Copy this day to all days below" aria-label={`Copy ${LABELS[idx]} to the days below`} className="text-muted-foreground hover:text-hive-burnt dark:hover:text-hive-orange">
            <Copy className="w-3.5 h-3.5" />
          </button>
        );
        // A day off is one quiet line, not a row of greyed boxes (Rob, 03/11/2026).
        if (!working) {
          return (
            <div key={day} style={GRID} className="px-1.5 py-1.5 min-h-[40px] border-t border-border">
              <span className="text-sm font-bold text-muted-foreground" title={LABELS[idx]}>{SHORT[idx]}</span>
              <span className="text-[13px] italic text-muted-foreground text-center" style={{ gridColumn: '2 / 5' }}>Not working</span>
              <span />
              <div className="flex items-center justify-end gap-3">
                <button type="button" onClick={() => setFullDay(day)} className="inline-flex items-center gap-1 text-xs font-bold text-hive-burnt dark:text-hive-orange hover:underline whitespace-nowrap">
                  <Plus className="w-3 h-3" />Add hours
                </button>
                {copy || <span className="w-3.5" />}
              </div>
            </div>
          );
        }
        return (
          <div key={day} style={GRID} className="px-1.5 py-1.5 min-h-[40px] border-t border-border">
            <span className="text-sm font-bold" title={LABELS[idx]}>{SHORT[idx]}</span>
            {renderBlock(day, idx, 'am')}
            <div className="flex flex-col items-center justify-center" title="The break is the gap between the morning and afternoon sessions. A paid break is working time — no gap, but still a break.">
              {gap === null
                ? <span className="h-6 w-px bg-border" />
                : gap < 0
                  ? <span className="text-xs font-medium text-destructive whitespace-nowrap">overlap!</span>
                  : (
                    <>
                      {gap > 0 && <span className="text-xs whitespace-nowrap text-foreground">{fmtMins(gap)}</span>}
                      {paid > 0 && <span className="text-xs whitespace-nowrap text-muted-foreground">{fmtMins(paid)} paid</span>}
                      {gap === 0 && paid === 0 && <span className="text-xs whitespace-nowrap text-muted-foreground">none</span>}
                    </>
                  )}
            </div>
            {renderBlock(day, idx, 'pm')}
            <span className="text-[13px] font-bold text-right tabular-nums">{fmtMins(Math.round(dayHours(d) * 60))}</span>
            <div className="flex items-center justify-end gap-3">
              {st !== 'Full day' && <span className="text-[11px] text-muted-foreground whitespace-nowrap">{st}</span>}
              {st !== 'Full day'
                ? (<button type="button" onClick={() => setFullDay(day)} className="text-xs font-bold text-hive-burnt dark:text-hive-orange hover:underline whitespace-nowrap">Full day</button>)
                : (<button type="button" onClick={() => setOff(day)} className="text-xs font-bold text-muted-foreground hover:underline whitespace-nowrap">Clear</button>)}
              {copy || <span className="w-3.5" />}
            </div>
          </div>
        );
      })}
      <div className="flex flex-wrap items-center justify-between gap-3 px-1.5 pt-3 mt-1 border-t border-border">
        <div className="flex items-center gap-2 text-[13px]">
          <span className="text-muted-foreground">Break</span>
          <Input
            type="number" min="0" step="5"
            value={pendingBreak !== null ? pendingBreak : (uniformLen === null ? '' : String(uniformLen))}
            placeholder={uniformLen === null && breakInfos.length > 0 ? 'mixed' : undefined}
            disabled={breakInfos.length === 0}
            onChange={(e) => onBreakLenType(e.target.value)}
            onBlur={(e) => { if (pendingBreak !== null) commitBreakLen(e.target.value); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitBreakLen(e.currentTarget.value); } }}
            className="input-hive w-[76px] h-[30px] text-sm"
            aria-label="Break length in minutes"
          />
          <span className="text-muted-foreground">min</span>
          <select
            value={uniformKind ?? 'mixed'}
            disabled={breakInfos.length === 0}
            onChange={(e) => onBreakKindChange(e.target.value)}
            className="input-hive hh-select hh-compact w-[104px] text-sm"
            aria-label="Break paid or unpaid"
          >
            <option value="unpaid">unpaid</option>
            <option value="paid">paid</option>
            {uniformKind === null && <option value="mixed" disabled>mixed</option>}
          </select>
          <span className="text-muted-foreground whitespace-nowrap">for every working day</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {totalBreak > 0 && (
            <span className="text-muted-foreground">{fmtMins(totalBreak)} unpaid breaks (not counted)</span>
          )}
          {totalPaidBreak > 0 && (
            <span className="text-muted-foreground">{fmtMins(totalPaidBreak)} paid breaks (counted)</span>
          )}
          <span className="text-[13px] font-bold bg-primary text-primary-foreground px-3 py-1 rounded-full whitespace-nowrap">
            {Number.isInteger(totalHours) ? totalHours : totalHours.toFixed(1)} hours a week
          </span>
        </div>
      </div>
      <p className="px-1.5 mt-2.5 text-xs text-muted-foreground flex items-start gap-1.5">
        <Info className="w-3.5 h-3.5 mt-px flex-shrink-0" />
        Repeats every week. Click a time to pick it, or type it (&ldquo;9&rdquo; or &ldquo;1330&rdquo;). An unpaid break
        is the gap between morning and afternoon; a paid break counts as work.
      </p>
      {warnings.length > 0 && (
        <div className="mx-1.5 mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-hive-pending-fg dark:text-amber-300">
            <AlertTriangle className="w-3.5 h-3.5" />
            Statutory rest-break advisory
          </div>
          <ul className="mt-1.5 space-y-1 text-xs text-muted-foreground">
            {warnings.map((w, i) => (
              <li key={w.weekday ?? i}>
                {w.message}
                {w.source_url && (
                  <> <a href={w.source_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">source</a></>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-xs text-muted-foreground">
            Advisory only — nothing is blocked. Compensatory rest or contractual arrangements may apply.
          </p>
        </div>
      )}
    </div>
  );
};

export default WorkingHoursEditor;
